"use client";

import { ArrowUpRight, Loader, Mail, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const contactMethods = [{
  label: "STANDORT",
  value: "Oldenburg, Deutschland",
  icon: MapPin
}, {
  label: "E-MAIL",
  value: "Jerome@orvoweb.de",
  icon: Mail
}, {
  label: "TELEFON",
  value: "+ 49 17641368728",
  icon: Phone
}];
function ReachOutForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-primary bg-primary/5">
        <p className="font-mono text-xs tracking-widest uppercase text-primary mb-3">
          [ NACHRICHT ERHALTEN ]
        </p>
        <p className="font-serif text-xl text-foreground mb-6 leading-snug">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-xs tracking-widest uppercase border-border bg-transparent hover:bg-secondary text-foreground">
          Neue Anfrage
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-7">
      <FormField control={control} name="inquiry_type" rules={{
      required: "Bitte wählen Sie einen Grund aus"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              01 / Anliegen
            </FormLabel>
            <span className="sr-only">Anliegen auswählen</span>
            <Select onValueChange={field.onChange} value={field.value || ""}>
              <FormControl>
                <SelectTrigger className="w-full h-12 bg-transparent border-0 border-b border-border px-0 font-default text-base text-foreground focus-visible:ring-0 focus-visible:border-primary">
                  <SelectValue placeholder="Wählen Sie eine Option" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="Website Erstellung" className="font-default">Website Erstellung</SelectItem>
                <SelectItem value="Website Redesign" className="font-default">Website Redesign</SelectItem>
                <SelectItem value="SEO Betreuung" className="font-default">SEO Betreuung</SelectItem>
                <SelectItem value="Sonstiges" className="font-default">Sonstiges</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
        <FormField control={control} name="name" rules={{
        required: "Name ist erforderlich"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                02 / Ihr Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Max Mustermann" {...field} className="h-12 bg-transparent border-0 border-b border-border px-0 font-default text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary" />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-Mail ist erforderlich",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ungültige E-Mail-Adresse"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                03 / E-Mail Adresse
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="Jerome@orvoweb.de" {...field} className="h-12 bg-transparent border-0 border-b border-border px-0 font-default text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary" />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              04 / Telefonnummer — Optional
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+49 123 456789" {...field} className="h-12 bg-transparent border-0 border-b border-border px-0 font-default text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Nachricht ist erforderlich",
      minLength: {
        value: 10,
        message: "Mindestens 10 Zeichen"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              05 / Nachricht
            </FormLabel>
            <FormControl>
              <Textarea rows={6} placeholder="Wie können wir Ihnen helfen?" {...field} className="min-h-[140px] bg-transparent border-0 border-b border-border px-0 py-3 font-default text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary resize-none" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive bg-destructive/10">
          <p className="font-mono text-xs text-destructive">{submitError}</p>
        </div>}

      <div className="pt-2">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-4">
          [ Wir antworten meist innerhalb von 24 Stunden ]
        </p>
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-default text-sm font-bold tracking-[0.15em] uppercase rounded-lg shadow-lg">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Wird gesendet...
            </> : <>Jetzt anfragen →</>}
        </Button>
      </div>
    </Form>;
}
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.1
  });
  return <section data-section-id="4170" id="strategische-beratung-anfragen" className="bg-background text-foreground relative overflow-hidden py-20 md:py-28 lg:py-32 px-4 sm:px-6 lg:px-10">
      <div ref={ref} className={`max-w-[1400px] mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-6 flex flex-col">
            <div className="flex items-center gap-3 mb-10">
              <span className="h-px w-10 bg-primary" />
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-primary">
                01 — Kontakt
              </p>
            </div>

            <h1 className="font-default font-bold uppercase leading-[0.9] tracking-tight text-foreground mb-10">
              <span className="block text-[clamp(3rem,8vw,9rem)]">JETZT /</span>
              <span className="block text-[clamp(3rem,8vw,9rem)]">PROJEKT /</span>
              <span className="block text-[clamp(3rem,8vw,9rem)] font-serif italic font-light">
                starten.
              </span>
            </h1>

            <p className="font-default text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl mb-12">
              Bereit für eine digitale Transformation? Wir freuen uns darauf, Ihr Unternehmen in Oldenburg und darüber hinaus mit modernem Webdesign auf das nächste Level zu heben. Entdecken Sie{" "}
              <Link to="/ServicesPage" className="text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
                Alle Services
              </Link>{" "}
              oder kehren Sie zurück zur{" "}
              <Link to="/HomePage" className="text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
                Home
              </Link>
              -Seite.
            </p>

            <div className="space-y-4">
              {contactMethods.map((method, i) => {
              const Icon = method.icon;
              return <div key={i} data-index={i} className="group relative bg-card border border-border overflow-hidden transition-all duration-500 hover:bg-card/80">
                    <span className="absolute left-0 top-0 h-full w-0 bg-primary transition-all duration-500 group-hover:w-1" />
                    <div className="flex items-center justify-between gap-6 px-6 py-6 md:px-8 md:py-7">
                      <div className="flex items-center gap-5 min-w-0">
                        <Icon className="size-4 text-primary shrink-0" strokeWidth={1.5} />
                        <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground shrink-0 hidden sm:inline">
                          {method.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 min-w-0 flex-1 justify-end">
                        <p className="font-default text-sm md:text-base text-foreground text-right truncate">
                          {method.value}
                        </p>
                        <ArrowUpRight className="size-4 text-primary shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative p-8 md:p-12 border border-border bg-card">
              <div className="flex items-center justify-between mb-10 pb-6 border-b border-border">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                  [ Ersteinschätzung ]
                </p>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-primary">
                  ORVO
                </p>
              </div>

              <div className="mb-10">
                <h2 className="font-default text-2xl md:text-3xl font-bold text-foreground mb-4">
                  Jetzt kostenlose Ersteinschätzung anfragen
                </h2>
                <p className="font-default text-muted-foreground leading-relaxed">
                  Egal ob Website-Redesign, SEO-Optimierung oder komplette Website-Neuentwicklung – wir beraten Sie gerne kostenlos zu Ihren Anforderungen und zeigen Ihnen Lösungen auf.
                </p>
              </div>

              <FormProvider formKey="strategic_consultation_contact" formId="strategic_consultation_contact" sectionName="strategische-beratung-anfragen" defaultValues={{
              name: "",
              email: "",
              phone: "",
              inquiry_type: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              inquiry_type: {
                required: true
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Jetzt anfragen" successMessage="Vielen Dank für Ihre Nachricht. Wir melden uns zeitnah bei Ihnen." errorMessage="Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.">
                <ReachOutForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}